import { Fragment, type ReactNode } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUp, ArrowUpDown, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { listItem, staggerChildren } from "../lib/motion";
import { EmptyState, ErrorState, SkeletonTable, Spinner } from "./Feedback";

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** Cell renderer. Return a node; formatting belongs to the caller. */
  cell: (row: T, index: number) => ReactNode;
  /** Enables the sort control in the header for this column. */
  sortable?: boolean;
  align?: "left" | "right" | "center";
  width?: string;
  /**
   * Columns hidden below this breakpoint on narrow screens. The card layout
   * used on mobile shows every column regardless, so nothing is lost.
   */
  hideBelow?: "sm" | "md" | "lg" | "xl";
  /** Header label used by the mobile card layout. */
  mobileLabel?: string;
}

export type SortDirection = "asc" | "desc";

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  /** Dims the current page instead of blanking it while refetching. */
  refreshing?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyIcon?: LucideIcon;
  emptyAction?: ReactNode;
  className?: string;
  /** Extra content rendered under each row on mobile cards. */
  caption?: string;
}

const hideBelowClasses = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
} as const;

const alignClasses = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

/**
 * The single table used across the admin surface.
 *
 * Below `md` it stops being a table and renders one card per row with each cell
 * labelled — horizontal scrolling on a phone is how operational tables become
 * unusable, so this trades density for legibility rather than adding a scrollbar.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  refreshing = false,
  error = null,
  onRetry,
  onRowClick,
  sort = null,
  onSortChange,
  emptyTitle = "Nothing to show",
  emptyDescription,
  emptyIcon,
  emptyAction,
  className,
  caption,
}: DataTableProps<T>) {
  if (error) {
    return (
      <div className={cn("rounded-xl border border-line bg-surface", className)}>
        <ErrorState message={error} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={cn("rounded-xl border border-line bg-surface p-4", className)}>
        <SkeletonTable rows={6} columns={Math.min(columns.length, 6)} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={cn("rounded-xl border border-line bg-surface", className)}>
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  const toggleSort = (columnId: string) => {
    if (!onSortChange) return;
    const nextDirection: SortDirection =
      sort?.columnId === columnId && sort.direction === "asc" ? "desc" : "asc";
    onSortChange({ columnId, direction: nextDirection });
  };

  return (
    <div
      className={cn(
        "relative rounded-xl border border-line bg-surface",
        className,
      )}
    >
      {refreshing && (
        <div className="absolute inset-0 z-10 grid place-items-center rounded-xl bg-surface/60 backdrop-blur-[1px]">
          <Spinner size={22} label="Refreshing" />
        </div>
      )}

      {/* Desktop: real table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full border-collapse text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-line">
              {columns.map((column) => {
                const isSorted = sort?.columnId === column.id;
                const SortIcon = !isSorted
                  ? ArrowUpDown
                  : sort.direction === "asc"
                    ? ArrowUp
                    : ArrowDown;

                return (
                  <th
                    key={column.id}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={
                      isSorted
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={cn(
                      "whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-[0.06em] text-content-subtle",
                      alignClasses[column.align ?? "left"],
                      column.hideBelow && hideBelowClasses[column.hideBelow],
                    )}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.id)}
                        className={cn(
                          "inline-flex items-center gap-1.5 whitespace-nowrap rounded transition-colors hover:text-content",
                          isSorted && "text-gold-700 dark:text-gold-400",
                        )}
                      >
                        {column.header}
                        <SortIcon size={13} aria-hidden="true" />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <motion.tbody
            variants={staggerChildren(0.025)}
            initial="hidden"
            animate="visible"
          >
            {rows.map((row, index) => (
              <motion.tr
                key={rowKey(row, index)}
                variants={listItem}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  "border-b border-line/70 last:border-0 transition-colors",
                  onRowClick && "cursor-pointer",
                  "hover:bg-gold-50/60 dark:hover:bg-gold-950/25",
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      "px-4 py-3 align-middle text-content",
                      alignClasses[column.align ?? "left"],
                      column.hideBelow && hideBelowClasses[column.hideBelow],
                    )}
                  >
                    {column.cell(row, index)}
                  </td>
                ))}
              </motion.tr>
            ))}
          </motion.tbody>
        </table>
      </div>

      {/* Mobile: one labelled card per row */}
      <motion.ul
        variants={staggerChildren(0.03)}
        initial="hidden"
        animate="visible"
        className="divide-y divide-line md:hidden"
      >
        {rows.map((row, index) => (
          <motion.li
            key={rowKey(row, index)}
            variants={listItem}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn("px-4 py-4", onRowClick && "cursor-pointer active:bg-surface-sunken")}
          >
            <dl className="grid grid-cols-[minmax(0,7rem)_1fr] gap-x-3 gap-y-2 text-sm">
              {columns.map((column) => (
                <Fragment key={column.id}>
                  <dt className="truncate text-xs font-medium uppercase tracking-wide text-content-subtle">
                    {column.mobileLabel ??
                      (typeof column.header === "string" ? column.header : "")}
                  </dt>
                  <dd className="min-w-0 text-content">{column.cell(row, index)}</dd>
                </Fragment>
              ))}
            </dl>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  );
}
